import React, { useState, useEffect } from 'react';
import SkillsAuditView from './SkillsAuditView';

const SkillsManagementView = ({ dataService, skills, onDataChange, capabilities, people }) => {
  const [activeTab, setActiveTab] = useState('manage');
  const [localSkills, setLocalSkills] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [editingSkill, setEditingSkill] = useState(null);
  const [showAddSkill, setShowAddSkill] = useState(false);
  const [newSkill, setNewSkill] = useState({
    id: '',
    name: '',
    description: '',
    category: '',
    related_certifications: []
  });
  const [newCertification, setNewCertification] = useState('');

  useEffect(() => {
    if (skills && skills.length > 0) {
      setLocalSkills(skills);
    }
    loadCategories();
  }, [skills]);

  const loadCategories = async () => {
    try {
      const skillsData = await dataService.loadSkills();
      if (skillsData && skillsData.categories) {
        setCategories(skillsData.categories);
      }
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const filteredSkills = localSkills.filter(skill => {
    const matchesCategory = selectedCategory === 'all' || skill.category === selectedCategory;
    const matchesSearch = skill.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         skill.description.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const handleAddSkill = async () => {
    try {
      const skillId = newSkill.name.toLowerCase()
        .replace(/[^a-z0-9\s]/g, '')
        .replace(/\s+/g, '-');
      
      const skillToAdd = {
        ...newSkill,
        id: skillId
      };

      const updatedSkills = [...localSkills, skillToAdd];
      await dataService.saveSkills(updatedSkills, categories);
      setLocalSkills(updatedSkills);
      setNewSkill({
        id: '',
        name: '',
        description: '',
        category: '',
        related_certifications: []
      });
      setNewCertification('');
      setShowAddSkill(false);
      onDataChange();
    } catch (error) {
      console.error('Error adding skill:', error);
      alert('Error adding skill. Please try again.');
    }
  };

  const handleEditSkill = async (updatedSkill) => {
    try {
      const updatedSkills = localSkills.map(skill => 
        skill.id === updatedSkill.id ? updatedSkill : skill
      );
      await dataService.saveSkills(updatedSkills, categories);
      setLocalSkills(updatedSkills);
      setEditingSkill(null);
      setNewCertification('');
      onDataChange();
    } catch (error) {
      console.error('Error updating skill:', error);
      alert('Error updating skill. Please try again.');
    }
  };

  const handleDeleteSkill = async (skillId) => {
    if (window.confirm('Are you sure you want to delete this skill? This will affect all capabilities and people using this skill.')) {
      try {
        const updatedSkills = localSkills.filter(skill => skill.id !== skillId);
        await dataService.saveSkills(updatedSkills, categories);
        setLocalSkills(updatedSkills);
        onDataChange();
      } catch (error) {
        console.error('Error deleting skill:', error);
        alert('Error deleting skill. Please try again.');
      }
    }
  };

  const addCertificationToSkill = (skill, certification) => {
    if (certification.trim() && !skill.related_certifications.includes(certification.trim())) {
      return {
        ...skill,
        related_certifications: [...skill.related_certifications, certification.trim()]
      };
    }
    return skill;
  };

  const removeCertificationFromSkill = (skill, certificationToRemove) => {
    return {
      ...skill,
      related_certifications: skill.related_certifications.filter(cert => cert !== certificationToRemove)
    };
  };

  const getCategoryColor = (category) => {
    const colors = {
      'Leadership & Management': 'bg-purple-100 text-purple-800',
      'Active Response': 'bg-red-100 text-red-800',
      'Forensics': 'bg-blue-100 text-blue-800',
      'Analysis': 'bg-green-100 text-green-800',
      'Malware Analysis': 'bg-orange-100 text-orange-800',
      'Intelligence': 'bg-indigo-100 text-indigo-800',
      'Threat Hunting': 'bg-yellow-100 text-yellow-800',
      'Detection & Response': 'bg-pink-100 text-pink-800',
      'Communication': 'bg-gray-100 text-gray-800',
      'Business Management': 'bg-teal-100 text-teal-800',
      'Process Improvement': 'bg-cyan-100 text-cyan-800'
    };
    return colors[category] || 'bg-gray-100 text-gray-800';
  };

  const renderSkillsManagement = () => (
    <div className="p-6">
      {/* Header with Add Button */}
      <div className="flex justify-between items-center mb-6">
        <div></div>
        <button
          onClick={() => {
            setShowAddSkill(true);
            setNewCertification('');
          }}
          className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
        >
          Add Skill
        </button>
      </div>
        {/* Filters */}
        <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Search Skills
            </label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name or description..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Category
            </label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Categories</option>
              {categories.map(category => (
                <option key={category.name} value={category.name}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Skills Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredSkills.map(skill => (
            <div key={skill.id} className="border border-gray-200 rounded-lg p-4">
              <div className="flex justify-between items-start mb-2">
                <h3 className="font-medium text-gray-900 text-sm">{skill.name}</h3>
                <div className="flex space-x-1">
                  <button
                    onClick={() => {
                      setEditingSkill(skill);
                      setNewCertification('');
                    }}
                    className="text-blue-600 hover:text-blue-800 text-xs"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDeleteSkill(skill.id)}
                    className="text-red-600 hover:text-red-800 text-xs"
                  >
                    Delete
                  </button>
                </div>
              </div>
              
              <span className={`inline-block px-2 py-1 text-xs rounded-full mb-2 ${getCategoryColor(skill.category)}`}>
                {skill.category}
              </span>
              
              <p className="text-sm text-gray-600 mb-3">{skill.description}</p>
              
              {skill.related_certifications && skill.related_certifications.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-gray-700 mb-1">Related Certifications:</p>
                  <div className="flex flex-wrap gap-1">
                    {skill.related_certifications.map(cert => (
                      <span key={cert} className="inline-block px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                        {cert}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {filteredSkills.length === 0 && (
          <div className="text-center py-8">
            <p className="text-gray-500">No skills found matching your criteria.</p>
          </div>
        )}
    </div>
  );

  return (
    <div className="bg-white shadow rounded-lg">
      <div className="px-6 py-4 border-b border-gray-200">
        <h2 className="text-lg font-medium text-gray-900">Skills Management</h2>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8 px-6" aria-label="Tabs">
          <button
            onClick={() => setActiveTab('manage')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'manage'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Manage Skills
          </button>
          <button
            onClick={() => setActiveTab('audit')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'audit'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Audit & Validation
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'manage' && renderSkillsManagement()}
      {activeTab === 'audit' && (
        <div className="p-6">
          <SkillsAuditView 
            skills={localSkills} 
            capabilities={capabilities || []} 
            people={people || []} 
          />
        </div>
      )}

      {/* Add Skill Modal */}
      {showAddSkill && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Add New Skill</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  value={newSkill.name}
                  onChange={(e) => setNewSkill({...newSkill, name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={newSkill.description}
                  onChange={(e) => setNewSkill({...newSkill, description: e.target.value})}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <select
                  value={newSkill.category}
                  onChange={(e) => setNewSkill({...newSkill, category: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Category</option>
                  {categories.map(category => (
                    <option key={category.name} value={category.name}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Related Certifications</label>
                <div className="space-y-2">
                  <div className="flex space-x-2">
                    <input
                      type="text"
                      value={newCertification}
                      onChange={(e) => setNewCertification(e.target.value)}
                      placeholder="Enter certification (e.g., GCIH, CISSP)"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          if (newCertification.trim()) {
                            setNewSkill(addCertificationToSkill(newSkill, newCertification));
                            setNewCertification('');
                          }
                        }
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        if (newCertification.trim()) {
                          setNewSkill(addCertificationToSkill(newSkill, newCertification));
                          setNewCertification('');
                        }
                      }}
                      className="px-3 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600"
                    >
                      Add
                    </button>
                  </div>
                  
                  {newSkill.related_certifications.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {newSkill.related_certifications.map(cert => (
                        <span key={cert} className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                          {cert}
                          <button
                            type="button"
                            onClick={() => setNewSkill(removeCertificationFromSkill(newSkill, cert))}
                            className="ml-1 text-blue-600 hover:text-blue-800"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowAddSkill(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAddSkill}
                disabled={!newSkill.name || !newSkill.category}
                className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50"
              >
                Add Skill
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Skill Modal */}
      {editingSkill && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-96 overflow-y-auto">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Edit Skill</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  value={editingSkill.name}
                  onChange={(e) => setEditingSkill({...editingSkill, name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={editingSkill.description}
                  onChange={(e) => setEditingSkill({...editingSkill, description: e.target.value})}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <select
                  value={editingSkill.category}
                  onChange={(e) => setEditingSkill({...editingSkill, category: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Category</option>
                  {categories.map(category => (
                    <option key={category.name} value={category.name}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Related Certifications</label>
                <div className="space-y-2">
                  <div className="flex space-x-2">
                    <input
                      type="text"
                      value={newCertification}
                      onChange={(e) => setNewCertification(e.target.value)}
                      placeholder="Enter certification (e.g., GCIH, CISSP)"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          if (newCertification.trim()) {
                            setEditingSkill(addCertificationToSkill(editingSkill, newCertification));
                            setNewCertification('');
                          }
                        }
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        if (newCertification.trim()) {
                          setEditingSkill(addCertificationToSkill(editingSkill, newCertification));
                          setNewCertification('');
                        }
                      }}
                      className="px-3 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600"
                    >
                      Add
                    </button>
                  </div>
                  
                  {editingSkill.related_certifications && editingSkill.related_certifications.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {editingSkill.related_certifications.map(cert => (
                        <span key={cert} className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                          {cert}
                          <button
                            type="button"
                            onClick={() => setEditingSkill(removeCertificationFromSkill(editingSkill, cert))}
                            className="ml-1 text-blue-600 hover:text-blue-800"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setEditingSkill(null)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => handleEditSkill(editingSkill)}
                disabled={!editingSkill.name || !editingSkill.category}
                className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SkillsManagementView;